const dialog = document.querySelector('[data-pv-cart]');
const configEl = document.getElementById('pv-cart-config');

if (dialog && configEl) {
  const config = JSON.parse(configEl.textContent);
  const strings = config.strings;
  const decodeEntities = (value) => {
    const box = document.createElement('textarea');
    let current = String(value ?? '');
    for (let pass = 0; pass < 4; pass += 1) {
      if (!/&(?:amp|lt|gt|quot|#39|#x27);/i.test(current)) break;
      box.innerHTML = current;
      const next = box.value;
      if (next === current) break;
      current = next;
    }
    return current;
  };
  for (const key of Object.keys(strings)) strings[key] = decodeEntities(strings[key]);
  const body = dialog.querySelector('[data-pv-cart-body]');
  const footer = dialog.querySelector('[data-pv-cart-footer]');
  const title = dialog.querySelector('[data-pv-cart-title]');
  const live = dialog.querySelector('[data-pv-cart-live]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let opener = null;
  let lastAtc = null;
  let seq = 0;
  let desired = null;
  let flushing = false;
  let lastCart = null;
  const pendingQty = new Map();

  const variantIdFrom = (key) => String(key || '').split(':')[0];

  const keyFor = (variantId, fallback) => {
    const items = lastCart?.items || [];
    const match = items.find((item) => String(item.variant_id || item.id) === String(variantId))
      || items.find((item) => variantIdFrom(item.key) === String(variantId));
    return match?.key || fallback;
  };

  const shopPath = (path) => {
    const root = window.Shopify?.routes?.root || '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    return base + path.replace(/^\//, '');
  };

  const fill = (template, values) => template.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '');

  const esc = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const formatWith = (cents, precision, thousands, decimal) => {
    const fixed = (Number(cents) / 100).toFixed(precision);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? whole + decimal + parts[1] : whole;
  };

  const formatMoney = (cents) => {
    const format = config.moneyFormat || '${{amount}}';
    const map = {
      amount: formatWith(cents, 2, ',', '.'),
      amount_no_decimals: formatWith(cents, 0, ',', '.'),
      amount_with_comma_separator: formatWith(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatWith(cents, 0, '.', ','),
      amount_with_apostrophe_separator: formatWith(cents, 2, "'", '.'),
      amount_no_decimals_with_space_separator: formatWith(cents, 0, ' ', '.'),
      amount_with_space_separator: formatWith(cents, 2, ' ', ','),
      amount_with_period_and_space_separator: formatWith(cents, 2, ' ', '.'),
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => map[key] ?? map.amount);
  };

  const announce = (message) => {
    live.textContent = '';
    requestAnimationFrame(() => {
      live.textContent = message;
    });
  };

  const nudgeFor = (count) => {
    if (count <= 0) return strings.nudge0;
    if (count === 1) return strings.nudge1;
    if (count === 2) return strings.nudge2;
    return strings.nudge3;
  };

  const addDiscount = (rows, title, amount) => {
    const value = Number(amount) || 0;
    if (!title || !value) return;
    rows.set(title, (rows.get(title) || 0) + value);
  };

  const discountRows = (cart) => {
    const rows = new Map();
    for (const app of cart.cart_level_discount_applications || []) {
      const nested = app.discount_application || app;
      addDiscount(rows, nested.title, app.total_allocated_amount || nested.total_allocated_amount || app.amount);
    }
    if (rows.size === 0) {
      for (const item of cart.items || []) {
        for (const alloc of item.line_level_discount_allocations || []) {
          addDiscount(rows, alloc.discount_application?.title || 'Discount', alloc.amount);
        }
      }
    }
    return [...rows.entries()].map(([label, amount]) => ({ label, amount }));
  };

  const imageOf = (item) => {
    if (typeof item.image === 'string' && item.image) return item.image;
    if (item.featured_image?.url) return item.featured_image.url;
    return '';
  };

  const variantOf = (item) => {
    const variant = item.variant_title;
    if (!variant || variant === 'Default Title') return '';
    return variant;
  };

  const syncCount = (cart) => {
    for (const el of document.querySelectorAll('[data-header-cart-count]')) {
      el.dataset.headerCartCount = String(cart.item_count);
      el.classList.toggle('visible', cart.item_count > 0);
    }
    window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
  };

  const setExpanded = (open) => {
    for (const link of document.querySelectorAll('.site-header-cart--button')) {
      link.setAttribute('aria-controls', 'pv-cart-drawer');
      link.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  };

  const suppressBanner = () => {
    const banner = document.querySelector('[data-site-header] > [data-atc-banner]');
    if (!banner || banner.dataset.pvClosing === 'true') return;
    banner.dataset.pvClosing = 'true';
    banner.setAttribute('inert', '');
    banner.setAttribute('aria-hidden', 'true');
    // Empire installs trapFocus when the open transition ends (~375ms). Closing
    // before that leaves the trap installed on a display:none banner, whose
    // focusable list is empty, so every later focusin throws. Close after the
    // trap exists so removeTrapFocus clears it.
    let closed = false;
    const close = () => {
      if (closed || !banner.isConnected) return;
      closed = true;
      banner.querySelector('[data-atc-banner-close]')?.click();
    };
    setTimeout(close, 700);
  };

  const render = (cart, announceUpdate, focusTarget) => {
    const count = cart.item_count || 0;
    const total = cart.total_price || 0;
    const rows = discountRows(cart);
    const discountSum = rows.reduce((sum, row) => sum + row.amount, 0);
    let subtotal = cart.original_total_price;
    if (subtotal - discountSum !== total) subtotal = cart.items_subtotal_price;
    if (subtotal - discountSum !== total) subtotal = total + discountSum;
    const threshold = Number(config.threshold) || 10000;
    const remainder = Math.max(0, threshold - total);
    const progress = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 100;
    const moneyTotal = formatMoney(total);

    title.textContent = fill(strings.title, { count: String(count) });

    const lines = [];
    for (const item of cart.items || []) {
      const name = item.product_title || item.title;
      const variant = variantOf(item);
      const each = fill(strings.each, { price: formatMoney(item.original_price ?? item.price) });
      const meta = variant ? `${variant} · ${each}` : each;
      const src = imageOf(item);
      const photo = src
        ? `<a class="pv-cart__photo-link" href="${esc(item.url)}"><img class="pv-cart__photo" src="${esc(src)}" alt="${esc(name)}" width="160" height="160" loading="lazy"></a>`
        : '';
      lines.push(`<li class="pv-cart__line">
        ${photo}
        <div class="pv-cart__line-copy">
          <a class="pv-cart__line-title" href="${esc(item.url)}">${esc(name)}</a>
          <p class="pv-cart__line-meta">${esc(meta)}</p>
          <div class="pv-cart__line-actions">
            <div class="pv-cart__qty">
              <button type="button" data-action="decrease" data-line-key="${esc(item.key)}" data-qty="${item.quantity - 1}" aria-label="${esc(fill(strings.decrease, { title: name }))}" ${item.quantity <= 1 ? 'disabled' : ''}>−</button>
              <output class="pv-cart__qty-value" aria-label="${esc(fill(strings.quantity, { title: name }))}">${item.quantity}</output>
              <button type="button" data-action="increase" data-line-key="${esc(item.key)}" data-qty="${item.quantity + 1}" aria-label="${esc(fill(strings.increase, { title: name }))}">+</button>
            </div>
            <button type="button" class="pv-cart__remove" data-action="remove" data-line-key="${esc(item.key)}" data-qty="0" aria-label="${esc(fill(strings.removeItem, { title: name }))}">${esc(strings.remove)}</button>
          </div>
        </div>
      </li>`);
    }

    const status = remainder > 0
      ? fill(strings.toGo, { amount: formatMoney(remainder) })
      : strings.unlocked;
    const empty = count === 0
      ? `<div class="pv-cart__empty">
          <h3 class="pv-cart__empty-title">${esc(strings.emptyTitle)}</h3>
          <p>${esc(strings.emptyText)}</p>
          <a class="pv-cart__browse" href="${esc(config.browseUrl)}">${esc(strings.keepBrowsing)}</a>
        </div>`
      : '';
    const shipping = strings.shippingHead
      ? `<section class="pv-cart__shipping" aria-label="${esc(strings.shippingLabel)}">
      <p class="pv-cart__shipping-head">${esc(strings.shippingHead)}</p>
      <p class="pv-cart__shipping-status" data-pv-cart-status>${esc(status)}</p>
      <div class="pv-cart__track" role="progressbar" aria-label="${esc(strings.shippingLabel)}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${progress}">
        <div class="pv-cart__fill" style="width:${progress}%"></div>
      </div>
    </section>`
      : '';

    body.innerHTML = `${shipping}
    <p class="pv-tier-nudge"><strong data-pv-cart-nudge>${esc(nudgeFor(count))}</strong><small>${esc(strings.nudgeNote)}</small></p>
    ${empty}
    <ul class="pv-cart__lines">${lines.join('')}</ul>`;

    if (count === 0) {
      footer.hidden = true;
      footer.innerHTML = '';
    } else {
      const discountHtml = rows.map((row) => `<div class="pv-cart__row pv-cart__row--discount"><span>${esc(row.label)}</span><span>−${esc(formatMoney(row.amount))}</span></div>`).join('');
      footer.hidden = false;
      footer.innerHTML = `<div class="pv-cart__row"><span>${esc(strings.subtotal)}</span><span data-pv-cart-subtotal>${esc(formatMoney(subtotal))}</span></div>
        ${discountHtml}
        <div class="pv-cart__row pv-cart__row--total"><span>${esc(strings.total)}</span><span data-pv-cart-total>${esc(moneyTotal)}</span></div>
        <form action="${esc(config.cartUrl)}" method="post">
          <button class="pv-cart__checkout" type="submit" name="checkout" data-pv-cart-checkout>${esc(fill(strings.checkout, { total: moneyTotal }))}</button>
        </form>
        <p class="pv-cart__note">${esc(strings.taxNote)}</p>
        <a class="pv-cart__view-cart" href="${esc(config.cartUrl)}">${esc(strings.viewCart)}</a>`;
    }

    if (announceUpdate) {
      announce(fill(strings.updated, { count: String(count), total: moneyTotal }));
    }

    if (focusTarget) {
      const next = dialog.querySelector(`[data-action="${focusTarget.action}"][data-line-key="${CSS.escape(focusTarget.key)}"]`);
      (next || dialog.querySelector('[data-pv-cart-close]'))?.focus();
    }
  };

  const showError = (message) => {
    body.innerHTML = `<p class="pv-cart__error">${esc(message)} <a href="${esc(config.cartUrl)}">${esc(strings.viewCart)}</a></p>`;
    footer.hidden = true;
    announce(message);
  };

  const readCart = async () => {
    let lastStatus = 0;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const response = await fetch(shopPath('cart.js'), {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: AbortSignal.timeout(8000),
      });
      if (response.ok) return response.json();
      lastStatus = response.status;
      if (attempt === 0 && (response.status === 429 || response.status === 503)) {
        await new Promise((resolve) => { setTimeout(resolve, 400); });
        continue;
      }
      break;
    }
    throw new Error(String(lastStatus));
  };

  const load = async (announceUpdate) => {
    const id = ++seq;
    const cart = await readCart();
    if (id !== seq) return null;
    lastCart = cart;
    render(cart, announceUpdate);
    syncCount(cart);
    return cart;
  };

  const pause = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

  const postChange = async (key, quantity) => {
    let lastError = new Error('cart');
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await fetch(shopPath('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
          signal: AbortSignal.timeout(8000),
        });
        if (response.ok) return response.json();
        lastError = new Error(String(response.status));
        if (response.status !== 429 && response.status !== 503) throw lastError;
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        lastError = error;
      }
      if (attempt < 2) await pause(400 * (attempt + 1));
    }
    throw lastError;
  };

  const flushQueue = async () => {
    if (flushing) return;
    flushing = true;
    dialog.setAttribute('aria-busy', 'true');
    try {
      while (desired) {
        const next = desired;
        desired = null;
        const id = ++seq;
        const lineKey = keyFor(next.variantId, next.key);
        const changed = await postChange(lineKey, next.quantity);
        let cart = changed;
        try {
          cart = await readCart();
        } catch {
          cart = changed;
        }
        lastCart = cart;
        if (id !== seq) continue;
        const more = Boolean(desired);
        render(cart, !more, more ? null : { key: keyFor(next.variantId, lineKey), action: next.action });
        syncCount(cart);
        if (!desired) pendingQty.delete(next.variantId);
      }
    } catch (error) {
      const pending = desired;
      desired = null;
      if (error?.name !== 'AbortError') {
        try {
          const cart = await readCart();
          lastCart = cart;
          render(cart, false, pending ? { key: keyFor(pending.variantId, pending.key), action: pending.action } : null);
          syncCount(cart);
        } catch {
          showError(strings.error);
        }
        announce(strings.error);
      }
    } finally {
      flushing = false;
      dialog.removeAttribute('aria-busy');
      if (desired) flushQueue();
    }
  };

  const changeLine = (key, quantity, action) => {
    const variantId = variantIdFrom(key);
    pendingQty.set(variantId, quantity);
    desired = { variantId, quantity, action, key };
    flushQueue();
  };

  const open = async (trigger) => {
    suppressBanner();
    if (trigger instanceof HTMLElement) opener = trigger;
    if (!dialog.open) dialog.showModal();
    setExpanded(true);
    try {
      await load(false);
    } catch {
      showError(strings.loadError);
    }
    const inside = dialog.contains(document.activeElement);
    if (!inside) dialog.querySelector('[data-pv-cart-close]')?.focus();
  };

  dialog.querySelector('[data-pv-cart-close]')?.addEventListener('click', () => dialog.close());

  dialog.addEventListener('click', (event) => {
    event.stopPropagation();
    if (event.target === dialog) {
      dialog.close();
      return;
    }
    const button = event.target.closest('[data-action]');
    if (!button || !dialog.contains(button) || button.disabled) return;
    const key = button.dataset.lineKey;
    const action = button.dataset.action;
    const variantId = variantIdFrom(key);
    let quantity = 0;
    if (action !== 'remove') {
      const box = button.closest('.pv-cart__qty');
      const output = box?.querySelector('output');
      const shown = Number(output?.textContent);
      const base = pendingQty.has(variantId) ? pendingQty.get(variantId) : (Number.isFinite(shown) ? shown : 1);
      quantity = action === 'increase' ? base + 1 : Math.max(1, base - 1);
      if (output) output.textContent = String(quantity);
      const decrease = box?.querySelector('[data-action="decrease"]');
      if (decrease) decrease.disabled = quantity <= 1;
    }
    changeLine(key, quantity, action);
  });

  dialog.addEventListener('touchstart', (event) => {
    event.stopPropagation();
  });

  dialog.addEventListener('close', () => {
    setExpanded(false);
    if (opener && document.contains(opener)) opener.focus();
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('.site-header-cart--button');
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    open(link);
  });

  document.addEventListener('click', (event) => {
    const banner = document.querySelector('[data-site-header] > [data-atc-banner]');
    if (!banner || !dialog.open || banner.contains(event.target)) return;
    event.stopImmediatePropagation();
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-product-atc], .product-form--atc-button');
    if (button) lastAtc = button;
  }, true);

  document.addEventListener('addToCartFlyout:complete', () => {
    const trigger = document.activeElement;
    const next = trigger instanceof HTMLElement && trigger !== document.body ? trigger : lastAtc;
    queueMicrotask(() => open(next));
  });

  document.addEventListener('product-form:cart:navigate', (event) => {
    event.preventDefault();
  }, true);

  document.addEventListener('product-form:submit:success', () => {
    open(document.activeElement instanceof HTMLElement ? document.activeElement : null);
  });

  document.addEventListener('focusin', (event) => {
    if (!dialog.open) return;
    const banner = document.querySelector('[data-site-header] > [data-atc-banner]');
    if (banner && banner.contains(event.target)) {
      dialog.querySelector('[data-pv-cart-close]')?.focus();
    }
  });

  const dragHandle = dialog.querySelector('[data-pv-cart-drag]');
  const grip = dialog.querySelector('[data-pv-cart-grip]');
  let dragStart = 0;
  let dragging = false;

  const onDragStart = (event) => {
    if (window.innerWidth > 719 || event.button > 0) return;
    dragging = true;
    dragStart = event.clientY;
  };
  const onDragMove = (event) => {
    if (!dragging || reduceMotion.matches) return;
    const delta = Math.max(0, event.clientY - dragStart);
    dialog.style.transform = `translateY(${delta}px)`;
  };
  const onDragEnd = (event) => {
    if (!dragging) return;
    dragging = false;
    const delta = event.clientY - dragStart;
    dialog.style.transform = '';
    if (delta > 80) dialog.close();
  };
  for (const handle of [grip, dragHandle]) {
    handle?.addEventListener('pointerdown', onDragStart);
    handle?.addEventListener('pointermove', onDragMove);
    handle?.addEventListener('pointerup', onDragEnd);
    handle?.addEventListener('pointercancel', onDragEnd);
  }

  setExpanded(false);
}
